import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FORM_ACTION_INVOKE_THRESHOLD } from '@constants/index'
import {
  connectorActionModuleSelector,
  connectorLastFormActionTimeSelector,
  connectorLastFormActionTime,
} from '@redux/slices/connectorSlice'
import { DateUtil } from '@utils/dateUtil'
import { logger } from '@utils/logger'

type InvokeActionCallback = () => void | Promise<void>
type UseInvokeActionReturn = (callback?: InvokeActionCallback) => void

/**
 * Invokes a callback then trigger the commerce onFormAction event to ensure session is alive.
 */
export const useInvokeAction = (): UseInvokeActionReturn => {
  const dispatch = useDispatch()
  const actionModule = useSelector(connectorActionModuleSelector)
  const lastTime = useSelector(connectorLastFormActionTimeSelector)

  const invokeAction = useCallback(
    (callback?: InvokeActionCallback) => {
      const isFormActionAvailable = typeof actionModule?.onFormAction === 'function'

      const result = callback?.()
      Promise.resolve(result).catch(logger.warn)

      if (!isFormActionAvailable) {
        return
      }

      // check time elapsed since last call and call onFormAction only if time has exceeded threshold
      const isWithinThreshold =
        lastTime && !DateUtil.isMaxTimeExceeded({ startTime: lastTime, maxTime: FORM_ACTION_INVOKE_THRESHOLD })

      if (isWithinThreshold) {
        return
      }

      // keep commerce session alive
      actionModule
        .onFormAction()
        .then(() => {
          dispatch(connectorLastFormActionTime())
        })
        .catch(e => {
          logger.warn('directbilling error on form action', e)
        })
    },
    [dispatch, actionModule, lastTime]
  )

  return invokeAction
}
